import { AddProfessionalModal } from '@modules/admin/component/add-professional-modal/AddProfessionalModal';
import LoginSignUpModal from '~/modules/user/component/login-signup-modal/LoginSignUpModal';
import { ClientOnly } from 'remix-utils';
import { useEffect, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTracker } from '@archipro-website/tracker';
import {
    uiStateAtomLoginModalAuthSource,
    uiStateAtomLoginModalOpened,
    uiStateAtomLoginModalOpenedByPrompt,
    uiStateAtomLoginModalRedirect,
    uiStateAtomLoginModalTitle,
} from '~/modules-rocco/root/states/ui-state-login-modal-opened';
import { uiStateAtomAddProfessionalModalOpened } from '~/modules-rocco/root/states/ui-state-add-professional-modal-opened';
import { useLoginPromptOnPageView } from '~/modules-rocco/user/hook/use-login-prompt-on-page-view';
import { LoginPromptSheet } from '~/modules-rocco/user/component/login-prompt-sheet/LoginPromptSheet';
import { SaveGateHost } from '~/modules-rocco/design-board/component/SaveGateHost';

const SiteWideModals = () => {
    useLoginPromptOnPageView();
    const [loginModalOpened, setLoginModalOpened] = useAtom(
        uiStateAtomLoginModalOpened,
    );
    const title = useAtomValue(uiStateAtomLoginModalTitle);
    const authSource = useAtomValue(uiStateAtomLoginModalAuthSource);
    const [addProfessionalModalOpened, setAddProfessionalModalOpened] = useAtom(
        uiStateAtomAddProfessionalModalOpened,
    );
    const redirect = useAtomValue(uiStateAtomLoginModalRedirect);
    const setLoginModalRedirect = useSetAtom(uiStateAtomLoginModalRedirect);
    const [loginModalOpenedByPrompt, setLoginModalOpenedByPrompt] = useAtom(
        uiStateAtomLoginModalOpenedByPrompt,
    );
    const tracker = useTracker();

    const loginModalOpenedAt = useRef<number | null>(null);
    useEffect(() => {
        if (loginModalOpened) {
            loginModalOpenedAt.current = Date.now();
        }
    }, [loginModalOpened]);

    const closeLoginModal = () => {
        setLoginModalOpened(false);
        setLoginModalRedirect(undefined);
        setLoginModalOpenedByPrompt(false);
    };

    // User closed the modal without authenticating (the X / cancel path).
    // Only the page-view prompt's own modal counts as a prompt dismissal —
    // modals opened from other entry points (header, sheet CTAs, enquiry)
    // never log one. Auth-success closes go through handleAuthSuccess and
    // must not log this either.
    const dismissLoginModal = () => {
        if (loginModalOpenedByPrompt) {
            const resolvedAuthSource = authSource || 'unknown';
            const msVisible =
                loginModalOpenedAt.current !== null
                    ? Date.now() - loginModalOpenedAt.current
                    : undefined;
            // ExtraData JSON + Source are the only payload keys that reach
            // the warehouse; the flat keys serve network QA.
            tracker.log('LoginPromptDismissed', {
                url: new URL(window.location.href),
                data: {
                    authSource: resolvedAuthSource,
                    variant: 'modal',
                    ...(msVisible !== undefined && { msVisible }),
                    Source: resolvedAuthSource,
                    ExtraData: JSON.stringify({
                        authSource: resolvedAuthSource,
                        variant: 'modal',
                        ...(msVisible !== undefined && { msVisible }),
                    }),
                },
            });
        }
        closeLoginModal();
    };

    const handleAuthSuccess = () => {
        if (authSource === 'footer') {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
        closeLoginModal();
    };

    return (
        <ClientOnly fallback={''}>
            {() => (
                <>
                    {addProfessionalModalOpened && (
                        <AddProfessionalModal
                            onClose={() => setAddProfessionalModalOpened(false)}
                            isOpen={addProfessionalModalOpened}
                        />
                    )}
                    <LoginPromptSheet />
                    <SaveGateHost />
                    {loginModalOpened && (
                        <LoginSignUpModal
                            login={{
                                open: true,
                                onCancel: dismissLoginModal,
                                onConfirm: closeLoginModal,
                                onAuthSuccess: handleAuthSuccess,
                                flow: null,
                                authSource: authSource || 'unknown',
                                redirect: redirect,
                                header: title,
                                subtitle:
                                    authSource === 'siteGate'
                                        ? 'Dream it, design it, build it. Like a Pro.'
                                        : undefined,
                            }}
                            forgotPassword={{
                                open: true,
                                // Cancelling from forgot-password is still a
                                // dismissal of the prompt-opened modal.
                                onCancel: dismissLoginModal,
                            }}
                        />
                    )}
                </>
            )}
        </ClientOnly>
    );
};

export default SiteWideModals;
